import { useEffect, useState } from "react";
import {
  Monitor, Smartphone, Tablet, Globe, MapPin, FileText, Loader2, Activity,
  TrendingDown, LogIn, LogOut, ExternalLink, Eye, MousePointerClick, Timer, Gauge,
} from "lucide-react";
import { accessApi } from "@/access/api";

const DEVICE_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

const fmtSec = (s) => {
  if (!s) return "0s";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const sinceNow = (iso) => {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s ago`;
    return `${Math.floor(sec / 60)}m ago`;
  } catch { return "—"; }
};

export default function AccessAnalytics() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState({ active: 0, recent: [] });
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    accessApi.analytics(days).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [days]);

  // Poll live visitors every 15s
  useEffect(() => {
    let alive = true;
    const tick = () => accessApi.liveVisitors().then(({ data }) => alive && setLive(data)).catch(() => {});
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-[12px] text-white/40">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading analytics…
      </div>
    );
  }

  const totalDev = data.devices.reduce((s, x) => s + x.n, 0) || 1;
  const totalBrw = data.browsers.reduce((s, x) => s + x.n, 0) || 1;
  const conversionRate = data.sessions
    ? ((data.submissions / data.sessions) * 100).toFixed(2)
    : "—";

  return (
    <div data-testid="access-analytics" className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-violet-300">Analytics</p>
          <h1 className="mt-1.5 font-display text-3xl font-black tracking-tight sm:text-4xl">Site insights</h1>
          <p className="mt-1.5 text-[13.5px] text-white/55">Live traffic, audience and conversion signals.</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          data-testid="analytics-range"
          className="rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-1.5 text-[12.5px] font-bold focus:border-violet-400/60 focus:outline-none"
        >
          {[7, 14, 30, 60, 90].map((d) => (
            <option key={d} value={d} className="bg-[#0a0a14]">Last {d} days</option>
          ))}
        </select>
      </header>

      {/* LIVE strip */}
      <section className="overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.08] via-teal-500/[0.04] to-transparent p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
            </span>
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-emerald-300">Live now</p>
              <p className="font-display text-3xl font-black tracking-tight tabular-nums sm:text-4xl">
                {live.active} <span className="text-[14px] font-bold text-white/55">{live.active === 1 ? "visitor" : "visitors"}</span>
              </p>
              <p className="mt-0.5 text-[11.5px] text-white/45">Active in the last 5 minutes · auto-refreshes every 15s</p>
            </div>
          </div>
        </div>

        {live.recent.length > 0 && (
          <ul className="mt-5 divide-y divide-white/[0.06]">
            {live.recent.slice(0, 6).map((r) => (
              <li key={r.session_id} className="flex items-center gap-3 py-2 text-[12.5px]">
                <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                <span className="flex-1 truncate font-mono text-white/85">{r.path}</span>
                <span className="hidden sm:inline text-white/45 truncate">{r.where}</span>
                <span className="hidden md:inline text-white/45 truncate">{r.device} · {r.browser}</span>
                <span className="text-white/35 tabular-nums">{sinceNow(r.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Headline KPI cards */}
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi Icon={Eye}              label="Pageviews"          value={data.pageviews}                accent="violet" />
        <Kpi Icon={MousePointerClick} label="Unique sessions"    value={data.sessions}                accent="blue" />
        <Kpi Icon={Timer}            label="Avg. session"       value={fmtSec(data.avg_session_seconds)} accent="fuchsia" />
        <Kpi Icon={TrendingDown}     label="Bounce rate"        value={`${data.bounce_rate}%`}        accent="rose"
             hint={data.bounce_rate > 70 ? "High — pages may not match intent" : "Healthy"}
        />
        <Kpi Icon={Activity}         label="Pages / session"    value={data.avg_pages_per_session}    accent="emerald" />
        <Kpi Icon={Gauge}             label="Avg. load"          value={`${data.avg_load_ms}ms`}      accent="amber" />
        <Kpi Icon={TrendingDown}     label="Conversion rate"    value={`${conversionRate}%`}          accent="sky"
             hint="Submissions ÷ sessions"
        />
        <Kpi Icon={Globe}            label="Submissions"        value={data.submissions}              accent="violet" />
      </section>

      {/* Entry vs Exit funnel */}
      <section className="grid gap-4 lg:grid-cols-2">
        <Card title="Entry pages" Icon={LogIn} sub="Where sessions begin">
          {data.entry_pages.map((p, i) => (
            <FunnelRow key={p.path} rank={i + 1} path={p.path} value={p.n} max={data.entry_pages[0]?.n} mono accent="emerald" />
          ))}
          {data.entry_pages.length === 0 && <Empty />}
        </Card>
        <Card title="Exit pages · drop-offs" Icon={LogOut} sub="Where sessions end + exit rate">
          {data.exit_pages.map((p, i) => (
            <ExitRow key={p.path} rank={i + 1} path={p.path} exits={p.exits} views={p.views} rate={p.exit_rate} />
          ))}
          {data.exit_pages.length === 0 && <Empty />}
        </Card>
      </section>

      {/* Top pages + referrers */}
      <section className="grid gap-4 lg:grid-cols-2">
        <Card title="Top pages" Icon={FileText} sub="By pageviews · with load time">
          {data.top_pages.map((p, i) => (
            <div key={p.path} className="py-2">
              <div className="flex items-center gap-2 text-[12.5px]">
                <span className="w-4 flex-none font-mono text-[10.5px] text-white/35">{i + 1}</span>
                <span className="flex-1 truncate font-mono text-white/85">{p.path}</span>
                <span className="text-[10.5px] text-white/45">{p.avg_load_ms}ms</span>
                <span className="ml-2 font-bold tabular-nums text-white">{p.views}</span>
              </div>
              <span className="mt-1 block h-1 overflow-hidden rounded-full bg-white/[0.05]">
                <span className="block h-full bg-gradient-to-r from-violet-500 to-fuchsia-500" style={{ width: `${Math.max(4, (p.views / (data.top_pages[0]?.views || 1)) * 100)}%` }} />
              </span>
            </div>
          ))}
          {data.top_pages.length === 0 && <Empty />}
        </Card>
        <Card title="Top referrers" Icon={ExternalLink} sub="Where traffic comes from">
          {data.referrers.map((r, i) => (
            <FunnelRow key={r.path} rank={i + 1} path={hostOf(r.path)} value={r.n} max={data.referrers[0]?.n} accent="sky" />
          ))}
          {data.referrers.length === 0 && (
            <p className="rounded-md border border-dashed border-white/10 px-3 py-3 text-center text-[11px] text-white/40">
              All visits are direct traffic so far.
            </p>
          )}
        </Card>
      </section>

      {/* Audience breakdown */}
      <section className="grid gap-4 lg:grid-cols-4">
        <Card title="Devices" Icon={Monitor}>
          {data.devices.map((d) => {
            const Icon = DEVICE_ICON[d.name] || Monitor;
            const pct = Math.round((d.n / totalDev) * 100);
            return <BarRow key={d.name} Icon={Icon} label={d.name} value={`${pct}%`} count={d.n} pct={pct} />;
          })}
          {data.devices.length === 0 && <Empty />}
        </Card>
        <Card title="Browsers" Icon={Globe}>
          {data.browsers.map((b) => {
            const pct = Math.round((b.n / totalBrw) * 100);
            return <BarRow key={b.name} label={b.name} value={`${pct}%`} count={b.n} pct={pct} />;
          })}
          {data.browsers.length === 0 && <Empty />}
        </Card>
        <Card title="Countries" Icon={Globe}>
          {data.countries.map((c) => (
            <BarRow key={c.name} label={c.name} count={c.n} />
          ))}
          {data.countries.length === 0 && <Empty />}
        </Card>
        <Card title="Cities" Icon={MapPin}>
          {data.cities.map((c) => (
            <BarRow key={c.name} label={c.name} count={c.n} />
          ))}
          {data.cities.length === 0 && <Empty />}
        </Card>
      </section>

      {/* Where leads come from */}
      <Card title="Pages that convert" Icon={FileText} sub="Submissions per page · best to worst">
        <div className="grid gap-2 sm:grid-cols-2">
          {data.submission_pages.map((p, i) => (
            <div key={p.path} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <span className="font-mono text-[10.5px] text-white/40">{String(i + 1).padStart(2, "0")}</span>
              <span className="flex-1 truncate font-mono text-[12.5px] text-white/85">{p.path}</span>
              <span className="font-display text-lg font-black tabular-nums">{p.n}</span>
            </div>
          ))}
          {data.submission_pages.length === 0 && <Empty />}
        </div>
      </Card>

      {/* DiSC persona path-selector clicks */}
      <PersonaCard personas={data.personas || []} personaPages={data.persona_pages || []} />
    </div>
  );
}

/* ─── DiSC persona breakdown ─── */
const PERSONA_META = {
  scale: { label: "I'm scaling",            disc: "D",   color: "from-rose-500 to-orange-500" },
  calm:  { label: "I'm drowning in admin",  disc: "S",   color: "from-emerald-500 to-teal-500" },
  comms: { label: "Losing patients",        disc: "i",   color: "from-violet-500 to-fuchsia-500" },
  data:  { label: "Show me the numbers",    disc: "C",   color: "from-blue-500 to-sky-500" },
  new:   { label: "I just opened a clinic", disc: "S",   color: "from-amber-500 to-yellow-500" },
  ai:    { label: "AI doing the work",      disc: "D/i", color: "from-fuchsia-500 to-indigo-500" },
};

const PersonaCard = ({ personas, personaPages }) => {
  const total = personas.reduce((s, p) => s + p.n, 0);
  return (
    <Card
      title="DiSC path-selector clicks"
      Icon={MousePointerClick}
      sub="Which DiSC persona is your audience self-selecting as. Higher count = clearer persona pull."
    >
      {total === 0 ? (
        <Empty />
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {personas.map((p) => {
            const meta = PERSONA_META[p.persona] || { label: p.persona, disc: "?", color: "from-violet-500 to-fuchsia-500" };
            const pct = Math.round((p.n / (total || 1)) * 100);
            const fromPages = personaPages.filter((pp) => pp.persona === p.persona).slice(0, 3);
            return (
              <div
                key={p.persona}
                data-testid={`persona-card-${p.persona}`}
                className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-bold tracking-tight text-white">
                      {meta.label}
                    </p>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">
                      Style · {meta.disc}
                    </p>
                  </div>
                  <span className="font-display text-2xl font-black tabular-nums">{p.n}</span>
                </div>
                <span className="mt-2 block h-1 overflow-hidden rounded-full bg-white/[0.05]">
                  <span className={`block h-full bg-gradient-to-r ${meta.color}`} style={{ width: `${Math.max(4, pct)}%` }} />
                </span>
                <p className="mt-1 text-[10.5px] text-white/45">{pct}% of clicks</p>
                {fromPages.length > 0 && (
                  <p className="mt-2 truncate text-[10.5px] text-white/45">
                    From: {fromPages.map((fp) => `${fp.path} (${fp.n})`).join(" · ")}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

/* ----- helpers ----- */
const hostOf = (url) => {
  try {
    const u = new URL(url);
    return u.host + u.pathname.replace(/\/$/, "");
  } catch { return url; }
};

const ACCENT_BG = {
  violet:  "from-violet-500 to-fuchsia-500",
  blue:    "from-blue-500 to-sky-500",
  amber:   "from-amber-500 to-orange-500",
  fuchsia: "from-fuchsia-500 to-pink-500",
  sky:     "from-sky-500 to-cyan-500",
  emerald: "from-emerald-500 to-teal-500",
  rose:    "from-rose-500 to-pink-500",
};

const Kpi = ({ Icon, label, value, hint, accent }) => (
  <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.025] p-4">
    <div className={`pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-br ${ACCENT_BG[accent] || ACCENT_BG.violet} opacity-[0.08] blur-2xl`} />
    <div className="flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-white/45" />
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">{label}</p>
    </div>
    <p className="mt-3 font-display text-[22px] font-black tracking-tight tabular-nums leading-none sm:text-[26px]">
      {value}
    </p>
    {hint && <p className="mt-1.5 text-[10.5px] text-white/40">{hint}</p>}
  </div>
);

const Card = ({ title, Icon, sub, children }) => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.025] p-5">
    <p className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/55">
      <Icon className="h-3.5 w-3.5 text-violet-300" /> {title}
    </p>
    {sub && <p className="mt-1 text-[11.5px] text-white/40">{sub}</p>}
    <div className="mt-4 space-y-1">{children}</div>
  </div>
);

const FunnelRow = ({ rank, path, value, max, mono, accent }) => {
  const pct = Math.round((value / (max || 1)) * 100);
  return (
    <div className="py-2">
      <div className="flex items-center gap-2 text-[12.5px]">
        <span className="w-4 flex-none font-mono text-[10.5px] text-white/35">{rank}</span>
        <span className={`flex-1 truncate text-white/85 ${mono ? "font-mono" : ""}`}>{path}</span>
        <span className="font-bold tabular-nums text-white">{value}</span>
      </div>
      <span className="mt-1 block h-1 overflow-hidden rounded-full bg-white/[0.05]">
        <span className={`block h-full bg-gradient-to-r ${ACCENT_BG[accent] || ACCENT_BG.violet}`} style={{ width: `${Math.max(4, pct)}%` }} />
      </span>
    </div>
  );
};

const ExitRow = ({ rank, path, exits, views, rate }) => (
  <div className="py-2.5 text-[12.5px]">
    <div className="flex items-center gap-2">
      <span className="w-4 flex-none font-mono text-[10.5px] text-white/35">{rank}</span>
      <span className="flex-1 truncate font-mono text-white/85">{path}</span>
      <span className="font-bold tabular-nums text-rose-200">{rate ?? "—"}%</span>
    </div>
    <p className="ml-6 mt-0.5 text-[10.5px] text-white/45">
      {exits} exit{exits === 1 ? "" : "s"} · {views} view{views === 1 ? "" : "s"}
    </p>
  </div>
);

const BarRow = ({ icon, Icon, label, value, count, pct }) => (
  <div className="flex items-center gap-2 py-1 text-[12.5px]">
    {Icon && <Icon className="h-3 w-3 text-violet-300" />}
    {icon}
    <span className="flex-1 truncate text-white/85">{label}</span>
    {typeof pct === "number" && (
      <span className="h-1 w-12 overflow-hidden rounded-full bg-white/[0.06]">
        <span className="block h-full bg-gradient-to-r from-violet-400 to-fuchsia-400" style={{ width: `${pct}%` }} />
      </span>
    )}
    <span className="ml-1 font-bold tabular-nums text-white">{value || count}</span>
  </div>
);

const Empty = () => (
  <p className="rounded-md border border-dashed border-white/10 px-3 py-3 text-center text-[11px] text-white/40">
    No data yet.
  </p>
);
